import { computed, ref } from 'vue';
import { useStructures, useTenant } from '@/core';
import { TenantConfig } from '@/types/interface/TenantConfig.interface';
import { useMemoize } from '@vueuse/core';
import type { MetaImage, MetaContentResponse } from '@/model';
import { fetchContent, resolveImage } from '@/services/useMeta/utils';
import i18n from '@/i18n';

export const META_BLOCKS_IMAGES = {
  image: 'meta-homepage-image',
  miniature: 'meta-homepage-miniature',
  logo: 'meta-logo',
};

export const META_BLOCKS = {
  content: 'meta-homepage-content',
  ...META_BLOCKS_IMAGES,
};

const content = ref('');
const image = ref<MetaImage>();
const miniature = ref<MetaImage>();
const logo = ref<MetaImage>();

const { tenant } = useTenant<TenantConfig>();
const { fetchStructuresChildren } = useStructures();
const { t } = i18n.global;

const parentSlugPath = computed(
  () => `${tenant.value.slugs && tenant.value.slugs.length ? tenant.value.slugs[0] : ''}/meta`,
);
const contentSlugPath = computed(() => `${parentSlugPath.value}/${META_BLOCKS.content}`);
const imageSlugPath = computed(() => `${parentSlugPath.value}/${META_BLOCKS.image}`);
const miniatureSlugPath = computed(() => `${parentSlugPath.value}/${META_BLOCKS.miniature}`);
const logoSlugPath = computed(() => `${parentSlugPath.value}/${META_BLOCKS.logo}`);

const loadMetadata = useMemoize(async () => {
  try {
    await fetchStructuresChildren(parentSlugPath.value, { limit: 100, filter: '{"type": "OBJECT"}' });
    await fetchMetadata();
  } catch (error) {
    console.error(error);
    console.info(`Missing meta information for '${parentSlugPath.value}', using defaults`);

    logo.value = {
      src: tenant.value.assets?.logo,
      alt: t('LOGO.ALT'),
    };

    throw error;
  }
});

const fetchMetadataData = useMemoize(async (language?: string) =>
  Promise.all([
    fetchContent<MetaContentResponse>(contentSlugPath.value, language),
    fetchContent(imageSlugPath.value, language),
    fetchContent(miniatureSlugPath.value, language),
    fetchContent(logoSlugPath.value, language),
  ]),
);

const fetchMetadata = async (language?: string) => {
  const [contentData, imageData, miniatureData, logoData] = await fetchMetadataData(language);

  content.value = contentData?.content?.content;

  image.value = await resolveImage(imageData);
  miniature.value = await resolveImage(miniatureData);
  logo.value = (await resolveImage(logoData)) || {
    src: tenant.value.assets?.logo,
    alt: t('LOGO.ALT'),
  };
};

const setLanguage = async (lang: string) => {
  try {
    await fetchMetadata(lang);
  } catch (err) {
    console.info('Failed to fetch metadata: ', err);
  }
};

export default () => ({
  content,
  image,
  miniature,
  logo,
  loadMetadata,
  setLanguage,
});
